import type { HTMLAttributes } from 'react';
import { cx, sizeClass, type Hue } from './shared';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  hue?: Hue;
  /** solid (default), soft or outline. */
  variant?: 'solid' | 'soft' | 'outline';
  size?: 'sm' | 'base' | 'lg';
  pill?: boolean;
  /** A number: centred and never narrower than it is tall. */
  count?: boolean;
  /** A status dot before the text. */
  dot?: boolean;
}

export function Badge({ hue, variant = 'solid', size, pill, count, dot, className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={cx(
        'badge',
        variant !== 'solid' && `badge--${variant}`,
        hue && `badge--${hue}`,
        sizeClass('badge', size),
        pill && 'badge--pill',
        count && 'badge--count',
        className,
      )}
      {...rest}
    >
      {dot && <span className="badge__dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

export interface DotProps extends HTMLAttributes<HTMLSpanElement> {
  hue?: Hue;
  glow?: boolean;
  pulse?: boolean;
  size?: 'sm' | 'base' | 'lg';
  /** Screen-reader text describing the status, e.g. "Online". */
  label?: string;
}

/** A standalone status indicator. */
export function Dot({ hue, glow, pulse, size, label, className, ...rest }: DotProps) {
  return (
    <span
      className={cx('dot', hue && `dot--${hue}`, glow && 'dot--glow', pulse && 'dot--pulse', sizeClass('dot', size), className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      {...rest}
    />
  );
}
